import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { AiSessionSettingsService } from '../ai-session-settings.service';

export interface AiSessionRelayUrlDialogData {
  initialUrl: string;
}

@Component({
  selector: 'lib-ai-session-relay-url-dialog',
  standalone: true,
  imports: [FormsModule, ButtonModule, InputTextModule, MessageModule, TranslateModule],
  template: `
    <form class="flex flex-col gap-4" (ngSubmit)="onSave()" autocomplete="off">
      <fieldset class="mt-4">
        <!--
        <label for="ai-session-relay-url" class="text-sm font-medium text-muted-color">
          {{ 'screens.ai_session.labels.relay_url' | translate }}
        </label>
        -->
        <div>
          <input
            pInputText
            id="ai-session-relay-url"
            class="w-full"
            type="url"
            [(ngModel)]="relayUrl"
            name="relayUrl"
            [placeholder]="'screens.ai_session.placeholders.relay_url' | translate"
          />
          <small class="block text-muted-color">{{ 'screens.ai_session.labels.relay_url_hint' | translate }}</small>
        </div>
      </fieldset>

      @if (errorMessage()) {
        <p-message severity="error" styleClass="w-full">
          {{ errorMessage() }}
        </p-message>
      }

      <div class="p-dialog-form-footer justify-end">
        <p-button
          class="flex-none"
          type="button"
          severity="secondary"
          [outlined]="true"
          [label]="'common.actions.cancel' | translate"
          (onClick)="onCancel()"
        />
        <p-button
          class="flex-none"
          type="submit"
          severity="primary"
          [outlined]="true"
          [label]="'common.actions.save' | translate"
          [disabled]="!relayUrl().trim()"
        />
      </div>
    </form>
  `,
})
export class AiSessionRelayUrlDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig<AiSessionRelayUrlDialogData>);
  private readonly settings = inject(AiSessionSettingsService);

  readonly relayUrl = signal(this.config.data?.initialUrl ?? '');
  readonly errorMessage = signal('');

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.errorMessage.set('');
    try {
      const normalized = this.settings.normalizeRelayUrl(this.relayUrl());
      this.dialogRef.close(normalized);
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : 'screens.ai_session.notifications.relay_invalid');
    }
  }
}
